import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from '../input';
import { Button } from '../button';
import { Badge } from '../badge';
import { Card, CardContent } from '../card';
import {
    MapPin,
    Search,
    Loader2,
    X,
    Navigation
} from 'lucide-react';
import { apiService } from '../../../services/api.service';
import { LocationFeature, LocationSearchOptions } from '../../../types/location';
import { cn } from '../utils';

interface LocationAutocompleteProps {
    onSelect: (location: LocationFeature) => void;
    onClear?: () => void;
    placeholder?: string;
    value?: string;
    searchOptions?: LocationSearchOptions;
    className?: string;
    showCurrentLocation?: boolean;
    disabled?: boolean;
}

export function LocationAutocomplete({
    onSelect,
    onClear,
    placeholder = 'Search for a location...',
    value = '',
    searchOptions = {},
    className,
    showCurrentLocation = true,
    disabled = false,
}: LocationAutocompleteProps) {
    const [query, setQuery] = useState(value);
    const [suggestions, setSuggestions] = useState<LocationFeature[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [error, setError] = useState<string | null>(null);

    const inputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<NodeJS.Timeout>();

    const debouncedSearch = useCallback(
        async (searchQuery: string) => {
            if (searchQuery.length < 2) {
                setSuggestions([]);
                setIsOpen(false);
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                const response = await apiService.searchLocations(searchQuery, {
                    limit: 5,
                    ...searchOptions,
                });
                setSuggestions(response.features);
                setIsOpen(true);
            } catch (err) {
                setError('Failed to search locations');
                setSuggestions([]);
                setIsOpen(false);
            } finally {
                setIsLoading(false);
            }
        },
        [searchOptions]
    );

    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            debouncedSearch(query);
        }, 300);

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [query, debouncedSearch]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setQuery(newValue);
        setSelectedIndex(-1);
    };

    const handleSelect = (location: LocationFeature) => {
        setQuery(location.place_name);
        setSuggestions([]);
        setIsOpen(false);
        setSelectedIndex(-1);
        onSelect(location);
    };

    const handleClear = () => {
        setQuery('');
        setSuggestions([]);
        setIsOpen(false);
        setSelectedIndex(-1);
        onClear?.();
        inputRef.current?.focus();
    };

    const handleCurrentLocation = () => {
        if (!navigator.geolocation) {
            setError('Geolocation is not supported');
            return;
        }

        setIsLoading(true);
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                try {
                    const response = await apiService.reverseGeocode({
                        longitude: position.coords.longitude,
                        latitude: position.coords.latitude,
                    });

                    if (response.features.length > 0) {
                        handleSelect(response.features[0]);
                    }
                } catch (err) {
                    setError('Failed to get current location');
                } finally {
                    setIsLoading(false);
                }
            },
            () => {
                setError('Permission denied for location access');
                setIsLoading(false);
            },
            { timeout: 10000, enableHighAccuracy: false }
        );
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen || suggestions.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev =>
                    prev < suggestions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && suggestions[selectedIndex]) {
                    handleSelect(suggestions[selectedIndex]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setSelectedIndex(-1);
                break;
        }
    };

    return (
        <div className={cn('relative w-full', className)}>
            <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    ref={inputRef}
                    type="text"
                    placeholder={placeholder}
                    value={query}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => suggestions.length > 0 && setIsOpen(true)}
                    onBlur={() => setTimeout(() => setIsOpen(false), 200)}
                    disabled={disabled}
                    className="pl-10 pr-20"
                />

                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                    {isLoading && (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    )}

                    {showCurrentLocation && !isLoading && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={handleCurrentLocation}
                            className="h-6 w-6 p-0"
                            disabled={disabled}
                        >
                            <Navigation className="h-3 w-3" />
                        </Button>
                    )}

                    {query && !isLoading && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={handleClear}
                            className="h-6 w-6 p-0"
                            disabled={disabled}
                        >
                            <X className="h-3 w-3" />
                        </Button>
                    )}
                </div>
            </div>

            {error && (
                <p className="mt-1 text-sm text-destructive">{error}</p>
            )}

            {isOpen && suggestions.length > 0 && (
                <Card className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto">
                    <CardContent className="p-0">
                        {suggestions.map((location, index) => (
                            <div
                                key={location.id}
                                className={cn(
                                    'flex cursor-pointer items-start gap-3 p-3 transition-colors hover:bg-accent',
                                    selectedIndex === index && 'bg-accent'
                                )}
                                onClick={() => handleSelect(location)}
                            >
                                <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm leading-none truncate">
                                        {location.text}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-1 truncate">
                                        {location.place_name}
                                    </p>
                                    {location.properties?.category && (
                                        <Badge variant="outline" className="mt-1 text-xs">
                                            {location.properties.category}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
